import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef, RowSelectionState, SortingState } from '@tanstack/react-table'
import { Download, Plus, Send } from 'lucide-react'
import { cx } from '@/lib/clases'
import { formatearFecha } from '@/lib/fecha'
import { formatearRNC } from '@/lib/rnc'
import { Boton } from '@/ui/Boton'
import { InsigniaDiscreta, InsigniaEstado } from '@/ui/InsigniaEstado'
import { ENCF, FechaCorta, Monto, RNC } from '@/ui/Numero'
import { TablaDatos, UMBRAL_VIRTUALIZACION } from '@/ui/tabla/TablaDatos'
import { useFiltrosEnUrl } from '@/ui/tabla/filtrosEnUrl'
import { PRESENTACION_ESTADO, bordeDeFila } from '@/dominio/documentos/estados'
import { nombreTipoECF, type Documento, type EstadoDocumento } from '@/dominio/documentos/tipos'
import { Etiqueta, Seccion } from './-componentes'
import { DOCUMENTOS_DE_MUESTRA } from './-datos'

/**
 * La tabla de datos en sus cuatro estados y sus tres densidades.
 *
 * Los filtros de esta pantalla viven en la URL: cambia el estado o el tipo y
 * mira la barra de direcciones. Esa vista se puede pegar en un chat.
 */
export const Route = createFileRoute('/sistema/tabla')({
  // Deja pasar los filtros de la tabla sin declararlos uno a uno: este es el
  // catálogo. Una pantalla real declara su esquema y lo valida con zod.
  validateSearch: (busqueda: Record<string, unknown>) => busqueda,
  component: CatalogoTabla,
})

type EstadoDeMuestra = 'datos' | 'muchos' | 'cargando' | 'error' | 'primera-vez' | 'sin-resultados' | 'resuelto'

const OPCIONES_DE_MUESTRA: readonly { valor: EstadoDeMuestra; etiqueta: string }[] = [
  { valor: 'datos', etiqueta: 'Con datos' },
  { valor: 'muchos', etiqueta: `Virtualizada (>${UMBRAL_VIRTUALIZACION})` },
  { valor: 'cargando', etiqueta: 'Cargando' },
  { valor: 'error', etiqueta: 'Con error' },
  { valor: 'primera-vez', etiqueta: 'Vacía · primera vez' },
  { valor: 'sin-resultados', etiqueta: 'Vacía · filtro sin resultados' },
  { valor: 'resuelto', etiqueta: 'Vacía · todo resuelto' },
]

const ESTADOS_FILTRABLES: readonly EstadoDocumento[] = [
  'aceptado',
  'en_proceso',
  'aceptado_condicional',
  'rechazado',
  'encolado',
  'en_contingencia',
]

function CatalogoTabla() {
  const [muestra, setMuestra] = useState<EstadoDeMuestra>('datos')
  const [orden, setOrden] = useState<SortingState>([{ id: 'fecha', desc: true }])
  const [seleccion, setSeleccion] = useState<RowSelectionState>({})
  const [pagina, setPagina] = useState(1)
  const [tamano, setTamano] = useState(50)

  const { filtros, aplicar, limpiar, hayFiltrosActivos } = useFiltrosEnUrl({
    estado: '',
    tipo: '',
  })

  const columnas = useMemo<ColumnDef<Documento, unknown>[]>(
    () => [
      {
        id: 'fecha',
        accessorFn: (fila) => fila.fecha,
        header: 'Fecha',
        meta: {
          etiqueta: 'Fecha',
          numerico: true,
          ancho: '100px',
          exportar: (fila) => formatearFecha(fila.fecha),
        },
        cell: ({ row }) => <FechaCorta valor={row.original.fecha} />,
      },
      {
        id: 'encf',
        accessorFn: (fila) => fila.eNCF ?? '',
        header: 'e-NCF',
        meta: {
          etiqueta: 'e-NCF',
          numerico: true,
          ancho: '165px',
          exportar: (fila) => fila.eNCF,
        },
        cell: ({ row }) => <ENCF valor={row.original.eNCF} />,
      },
      {
        id: 'tipo',
        accessorFn: (fila) => fila.tipo,
        header: 'Tipo',
        meta: {
          etiqueta: 'Tipo',
          ancho: '160px',
          exportar: (fila) => `${fila.tipo} ${nombreTipoECF(fila.tipo)}`,
        },
        cell: ({ row }) => (
          <span className="flex items-baseline gap-2">
            <span className="mono text-gris-600">{row.original.tipo}</span>
            <span className="truncate text-gris-900">{nombreTipoECF(row.original.tipo)}</span>
          </span>
        ),
      },
      {
        id: 'contraparte',
        accessorFn: (fila) => fila.contraparteNombre,
        header: 'Cliente',
        meta: {
          etiqueta: 'Cliente',
          exportar: (fila) => fila.contraparteNombre,
        },
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2">
            {/* Nada se rompe con un nombre de cliente de 80 caracteres. */}
            <span className="truncate text-gris-900">{row.original.contraparteNombre}</span>
            {row.original.origen === 'migrado' ? (
              <InsigniaDiscreta>migrado</InsigniaDiscreta>
            ) : null}
          </span>
        ),
      },
      {
        id: 'rnc',
        accessorFn: (fila) => fila.contraparteRNC,
        header: 'RNC',
        meta: {
          etiqueta: 'RNC',
          numerico: true,
          ancho: '130px',
          exportar: (fila) => formatearRNC(fila.contraparteRNC),
        },
        cell: ({ row }) => <RNC valor={row.original.contraparteRNC} />,
      },
      {
        id: 'total',
        accessorFn: (fila) => fila.total,
        header: 'Monto',
        meta: {
          etiqueta: 'Monto',
          numerico: true,
          ancho: '130px',
          exportar: (fila) => fila.total,
        },
        cell: ({ row }) => <Monto valor={row.original.total} />,
      },
      {
        id: 'estado',
        accessorFn: (fila) => fila.estado,
        header: 'Estado',
        enableSorting: false,
        meta: {
          etiqueta: 'Estado',
          ancho: '225px',
          exportar: (fila) => PRESENTACION_ESTADO[fila.estado].etiqueta,
        },
        cell: ({ row }) => {
          const presentacion = PRESENTACION_ESTADO[row.original.estado]
          return (
            <InsigniaEstado
              tono={presentacion.tono}
              aclaracion={presentacion.aclaracion}
              titulo={presentacion.descripcion}
            >
              {presentacion.etiqueta}
            </InsigniaEstado>
          )
        },
      },
    ],
    [],
  )

  const filtrados = useMemo(() => {
    let lista = DOCUMENTOS_DE_MUESTRA
    if (filtros.estado !== '') lista = lista.filter((d) => d.estado === filtros.estado)
    if (filtros.tipo !== '') lista = lista.filter((d) => d.tipo === filtros.tipo)
    return lista
  }, [filtros])

  const datos = useMemo(() => {
    if (muestra === 'muchos') return filtrados
    if (muestra === 'datos') return filtrados.slice(0, 18)
    return []
  }, [muestra, filtrados])

  const vacioPorMuestra =
    muestra === 'resuelto'
      ? { variante: 'resuelto' as const, mensaje: 'Sin pendientes.' }
      : muestra === 'sin-resultados'
        ? {
            variante: 'sin-resultados' as const,
            mensaje: 'Ninguna factura entre el 1 y el 15 de octubre.',
            accion: (
              <Boton variante="secundario" tamano="menor" onClick={limpiar}>
                Limpiar filtros
              </Boton>
            ),
          }
        : {
            variante: 'primera-vez' as const,
            mensaje: 'Aún no has emitido facturas.',
            accion: (
              <Boton variante="primario" icono={<Plus />}>
                Nueva factura
              </Boton>
            ),
          }

  return (
    <div className="flex flex-col gap-6">
      <Seccion
        titulo="Los cuatro estados"
        nota="Con datos, vacía en sus tres variantes, cargando con esqueletos del alto correcto y con error. Los cuatro los resuelve el componente de tabla: ninguna pantalla los reinventa."
      >
        <div className="flex flex-wrap items-center gap-2">
          {OPCIONES_DE_MUESTRA.map((opcion) => (
            <button
              key={opcion.valor}
              type="button"
              aria-pressed={muestra === opcion.valor}
              onClick={() => setMuestra(opcion.valor)}
              className={cx(
                'h-[var(--alto-control-menor)] rounded-md border px-2 text-etiqueta',
                muestra === opcion.valor
                  ? 'border-marca bg-marca-tenue font-medium text-marca'
                  : 'border-gris-300 bg-white text-gris-700 hover:bg-gris-50',
              )}
            >
              {opcion.etiqueta}
            </button>
          ))}
        </div>
      </Seccion>

      <TablaDatos<Documento>
        idTabla="catalogo-documentos"
        columnas={columnas}
        datos={datos}
        obtenerId={(fila) => fila.id}
        titulo="Documentos"
        cargando={muestra === 'cargando'}
        error={
          muestra === 'error'
            ? {
                titulo: 'No pudimos cargar los documentos.',
                contexto: 'El servidor respondió con un error. Tu trabajo no se perdió.',
                detalleTecnico: '{"estado":503,"codigo":"upstream_indisponible","trackId":"a7f3-9b2c"}',
              }
            : null
        }
        onReintentar={() => setMuestra('datos')}
        vacio={vacioPorMuestra}
        orden={orden}
        onOrdenChange={setOrden}
        seleccionable
        seleccion={seleccion}
        onSeleccionChange={setSeleccion}
        accionesSeleccion={(ids) => (
          <>
            <Boton variante="secundario" tamano="menor" icono={<Download />}>
              Descargar {ids.length} en lote
            </Boton>
            <Boton variante="secundario" tamano="menor" icono={<Send />}>
              Reenviar por correo
            </Boton>
          </>
        )}
        paginacion={{ pagina, tamano, total: muestra === 'muchos' ? filtrados.length : datos.length }}
        onPaginacionChange={(siguiente) => {
          setPagina(siguiente.pagina)
          setTamano(siguiente.tamano)
        }}
        nombreExportacion="documentos"
        hayFiltrosActivos={hayFiltrosActivos}
        onLimpiarFiltros={limpiar}
        filtros={
          <>
            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Estado
              <select
                value={filtros.estado}
                onChange={(evento) => aplicar({ estado: evento.target.value })}
                className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
              >
                <option value="">Todos</option>
                {ESTADOS_FILTRABLES.map((estado) => (
                  <option key={estado} value={estado}>
                    {PRESENTACION_ESTADO[estado].etiqueta}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex items-center gap-1 text-etiqueta text-gris-600">
              Tipo
              <select
                value={filtros.tipo}
                onChange={(evento) => aplicar({ tipo: evento.target.value })}
                className="h-[var(--alto-control-menor)] rounded-md border border-gris-500 bg-white px-1 text-etiqueta text-gris-900"
              >
                <option value="">Todos</option>
                {['31', '32', '33', '34', '41', '46'].map((tipo) => (
                  <option key={tipo} value={tipo}>
                    {tipo} · {nombreTipoECF(tipo)}
                  </option>
                ))}
              </select>
            </label>
          </>
        }
        // La fila de un rechazado muestra el motivo sin tener que entrar.
        segundaLinea={(fila) =>
          fila.estado === 'rechazado' && fila.motivoRechazo ? (
            <span className="text-rojo" title={fila.motivoRechazo}>
              {fila.motivoRechazo}
            </span>
          ) : null
        }
        bordeFila={(fila) => bordeDeFila(fila.estado)}
      />

      <Seccion
        titulo="Lo que esta tabla ya resuelve"
        nota="Todo esto está dentro del componente. Una pantalla nueva lo hereda sin escribir una línea."
      >
        <ul className="grid grid-cols-2 gap-x-6 gap-y-1 text-cuerpo text-gris-700">
          <li>Columnas configurables y recordadas por usuario</li>
          <li>Tres densidades: cómoda 44px, normal 36px, compacta 28px</li>
          <li>
            Filtros en la URL <Etiqueta>mira la barra de direcciones</Etiqueta>
          </li>
          <li>Orden del lado del servidor, con aria-sort</li>
          <li>Selección múltiple con acciones en lote</li>
          <li>Exportación a CSV con punto y coma y BOM para Excel</li>
          <li>Paginación del lado del servidor</li>
          <li>Virtualización sobre {UMBRAL_VIRTUALIZACION} filas</li>
          <li>Marcado semántico: table, thead, th scope, aria-rowcount</li>
          <li>Segunda línea de fila para el motivo del rechazo</li>
        </ul>
      </Seccion>
    </div>
  )
}
